import React, { useEffect, useState } from 'react';
import { Alert, Badge, Card, Col, Container, Row } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaChartLine,
  FaChartPie,
  FaCheckCircle, FaClipboardList, FaRedo, FaSpinner,
  FaTachometerAlt
} from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';

function JBranchAuditorDashboard() {
  const [stats, setStats] = useState({
    pendingChecklists: 0,
    inProgressChecklists: 0,
    completedChecklists: 0,
    reauditChecklists: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [auditor, setAuditor] = useState(null);
  const [profileImage, setProfileImage] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        // Get auditor data from local storage
        const auditorData = JSON.parse(localStorage.getItem('auditor'));
        setAuditor(auditorData);
        
        if (!auditorData || !auditorData.id || !auditorData.BranchCode) {
          throw new Error('Auditor data not found');
        }

        // Try to fetch profile image if available
        if (auditorData.id) {
          try {
            const profileResponse = await fetch(`${BASE_URL}/get-auditor/${auditorData.id}`);
            if (profileResponse.ok) {
              const profileData = await profileResponse.json();
              if (profileData.profileImage) {
                setProfileImage(`${BASE_URL}${profileData.profileImage}`);
              }
            }
          } catch (profileError) {
            console.error("Failed to load profile image:", profileError);
            // Continue with stats loading even if profile image fails
          }
        }
        
        const response = await fetch(
          `${BASE_URL}/get-auditor-dashboard-stats/${auditorData.id}/${auditorData.BranchCode}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch dashboard statistics');
        }
        
        const data = await response.json();
        
        if (data.success) {
          setStats(data.stats);
        } else {
          throw new Error(data.error || 'Failed to fetch dashboard statistics');
        }
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, [BASE_URL]);

  const totalChecklists = stats.pendingChecklists + stats.inProgressChecklists + stats.completedChecklists;

  // Calculate percentages safely (prevent division by zero)
  const pendingPercentage = totalChecklists ? Math.round((stats.pendingChecklists / totalChecklists) * 100) : 0;
  const inProgressPercentage = totalChecklists ? Math.round((stats.inProgressChecklists / totalChecklists) * 100) : 0;
  const completedPercentage = totalChecklists ? Math.round((stats.completedChecklists / totalChecklists) * 100) : 0;

  const getAuditorInitials = () => {
    if (!auditor) return "AU";
    return `${auditor.firstName?.charAt(0) || ''}${auditor.lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const handleStatCardClick = (status) => {
    navigate('/JBranchAuditChecklist', { state: { activeTab: status } });
  };

  const handleReauditCardClick = () => {
    navigate('/JBranchReAuditChecklist', { state: { activeTab: 'all' } });
  };
  
  return (
    <Container fluid className="p-0">
      {/* Dashboard Header */}
      <div className="dashboard-header bg-primary text-white">
        <Container fluid className="px-4 py-4">
          <Row className="align-items-center">
            <Col lg={6}>
              <div className="d-flex align-items-center">
                {profileImage ? (
                  <div className="auditor-avatar me-3">
                    <img 
                      src={profileImage} 
                      alt={`${auditor?.firstName} ${auditor?.lastName}`}
                      className="profile-image"
                    />
                  </div>
                ) : (
                  <div className="auditor-avatar me-3">
                    {getAuditorInitials()}
                  </div>
                )}
                <div>
                  <h4 className="greeting mb-0">Welcome back, {auditor?.firstName || 'Auditor'}!</h4>
                  <p className="mb-0 opacity-75">
                    <FaCalendarAlt className="me-1" size={14} /> {currentDate}
                  </p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="text-lg-end mt-3 mt-lg-0">
              <Badge bg="white" text="primary" className="stats-badge">
                <FaTachometerAlt className="me-1" /> Dashboard Overview
              </Badge>
            </Col>
          </Row>
        </Container>
      </div>
      
      {/* Dashboard Content */}
      <Container fluid className="px-4 py-4 dashboard-content">
        {error && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading><FaCheckCircle className="me-2" /> Error Loading Dashboard</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading Dashboard</p>
            <p className="text-muted">Please wait while we fetch your stats...</p>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <Row className="g-4 mb-4">
              <Col xs={12}>
                <h5 className="section-title mb-3">
                  <FaChartLine className="me-2" /> Audit Statistics
                </h5>
              </Col>
              
              {/* Pending Card */}
              <Col xs={12} sm={6} md={6} lg={3}>
                <Card className="stat-card border-0 shadow-sm h-100 clickable-card" onClick={() => handleStatCardClick('pending')}>
                  <Card.Body>
                    <div className="d-flex align-items-center mb-3">
                      <div className="stat-icon pending-icon">
                        <FaClipboardList />
                      </div>
                      <h5 className="stat-label ms-3 mb-0">Pending Audits</h5>
                    </div>
                    <h3 className="stat-value">{stats.pendingChecklists}</h3>
                  </Card.Body>
                </Card>
              </Col>
              
              {/* In Progress Card */}
              <Col xs={12} sm={6} md={6} lg={3}>
                <Card className="stat-card border-0 shadow-sm h-100 clickable-card" onClick={() => handleStatCardClick('in progress')}>
                  <Card.Body>
                    <div className="d-flex align-items-center mb-3">
                      <div className="stat-icon progress-icon">
                        <FaSpinner />
                      </div>
                      <h5 className="stat-label ms-3 mb-0">In Progress</h5>
                    </div>
                    <h3 className="stat-value">{stats.inProgressChecklists}</h3>
                  </Card.Body>
                </Card>
              </Col>
              
              {/* Completed Card */}
              <Col xs={12} sm={6} md={6} lg={3}>
                <Card className="stat-card border-0 shadow-sm h-100 clickable-card" onClick={() => handleStatCardClick('completed')}>
                  <Card.Body>
                    <div className="d-flex align-items-center mb-3">
                      <div className="stat-icon completed-icon">
                        <FaCheckCircle />
                      </div>
                      <h5 className="stat-label ms-3 mb-0">Completed Audits</h5>
                    </div>
                    <h3 className="stat-value">{stats.completedChecklists}</h3>
                  </Card.Body>
                </Card>
              </Col>
              
              {/* Re-audits Card */}
              <Col xs={12} sm={6} md={6} lg={3}>
                <Card className="stat-card border-0 shadow-sm h-100 clickable-card" onClick={handleReauditCardClick}>
                  <Card.Body>
                    <div className="d-flex align-items-center mb-3">
                      <div className="stat-icon reaudit-icon">
                        <FaRedo />
                      </div>
                      <h5 className="stat-label ms-3 mb-0">Re-audit Checklists</h5>
                    </div>
                    <h3 className="stat-value">{stats.reauditChecklists}</h3>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            {/* Summary and Chart */}
            <Row className="g-4 mb-4">
              {/* Total Summary */}
              <Col lg={4} md={12}>
                <Card className="border-0 shadow-sm h-100 summary-card">
                  <Card.Body>
                    <h5 className="mb-4">
                      <FaChartPie className="me-2" /> Audit Summary
                    </h5>
                    
                    <div className="total-container text-center mb-4">
                      <div className="total-circle mb-2">
                        {totalChecklists}
                      </div>
                      <h6 className="text-muted">Total Checklists</h6>
                    </div>
                    
                    <div className="summary-details">
                      <div className="summary-item d-flex align-items-center mb-3">
                        <div className="summary-indicator pending"></div>
                        <div className="summary-text">Pending</div>
                        <div className="summary-count ms-auto">{stats.pendingChecklists}</div>
                        <div className="summary-percentage ms-2">{pendingPercentage}%</div>
                      </div>
                      
                      <div className="summary-item d-flex align-items-center mb-3">
                        <div className="summary-indicator progress"></div>
                        <div className="summary-text">In Progress</div>
                        <div className="summary-count ms-auto">{stats.inProgressChecklists}</div>
                        <div className="summary-percentage ms-2">{inProgressPercentage}%</div>
                      </div>
                      
                      <div className="summary-item d-flex align-items-center">
                        <div className="summary-indicator completed"></div>
                        <div className="summary-text">Completed</div>
                        <div className="summary-count ms-auto">{stats.completedChecklists}</div>
                        <div className="summary-percentage ms-2">{completedPercentage}%</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              
              {/* Quick Actions */}
              <Col lg={8} md={12}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <h5 className="mb-4">Quick Actions</h5>
                    
                    <Row className="gy-4">
                      <Col md={6}>
                        <Link to="/AuditChecklist" className="quick-action-card audit-card">
                          <div className="quick-action-icon">
                            <FaClipboardList size={24} />
                          </div>
                          <div className="quick-action-content">
                            <h5 className="mb-1">Audit Checklists</h5>
                            <p className="mb-0 text-muted">
                              Manage and view your assigned audit checklists
                            </p>
                          </div>
                        </Link>
                      </Col>
                      
                      <Col md={6}>
                        <Link to="/ReAuditChecklist" className="quick-action-card reaudit-card">
                          <div className="quick-action-icon">
                            <FaRedo size={24} />
                          </div>
                          <div className="quick-action-content">
                            <h5 className="mb-1">Re-Audit Checklists</h5>
                            <p className="mb-0 text-muted">
                              Manage follow-up audits and track corrections
                            </p>
                          </div>
                        </Link>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>

      <style>
        {`
          /* Dashboard Header Styles */
          .dashboard-header {
            background-image: linear-gradient(135deg, var(--bs-primary) 0%, #4361ee 100%);
            border-bottom-left-radius: 20px;
            border-bottom-right-radius: 20px;
            box-shadow: 0 4px 12px rgba(var(--bs-primary-rgb), 0.1);
          }
          
          .greeting {
            font-weight: 600;
          }
          
          .auditor-avatar {
            width: 48px;
            height: 48px;
            background-color: rgba(255, 255, 255, 0.2);
            border: 2px solid rgba(255, 255, 255, 0.5);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 18px;
            color: white;
            overflow: hidden;
          }
          
          .profile-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          
          .stats-badge {
            padding: 8px 16px;
            font-weight: 500;
            border-radius: 30px;
          }
          
          /* Dashboard Content Styles */
          .dashboard-content {
            background-color: #f8f9fa;
            min-height: calc(100vh - 82px);
          }
          
          .section-title {
            font-weight: 600;
            color: #344767;
            display: flex;
            align-items: center;
          }
          
          /* Stat Cards */
          .stat-card {
            border-radius: 12px;
            overflow: hidden;
            transition: all 0.3s ease;
          }
          
          .stat-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
          }

          .clickable-card {
            cursor: pointer;
            user-select: none;
          }

          .clickable-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.15) !important;
          }

          .clickable-card:active {
            transform: translateY(-2px);
          }
          
          .stat-icon {
            width: 45px;
            height: 45px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
          }
          
          .pending-icon {
            background-color: rgba(255, 193, 7, 0.1);
            color: #ffc107;
          }
          
          .progress-icon {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .completed-icon {
            background-color: rgba(25, 135, 84, 0.1);
            color: #198754;
          }
          
          .reaudit-icon {
            background-color: rgba(220, 53, 69, 0.1);
            color: #dc3545;
          }
          
          .stat-percentage {
            font-weight: 600;
            font-size: 14px;
            padding: 4px 8px;
            border-radius: 20px;
          }
          
          .pending-percentage {
            background-color: rgba(255, 193, 7, 0.1);
            color: #ffc107;
          }
          
          .progress-percentage {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .completed-percentage {
            background-color: rgba(25, 135, 84, 0.1);
            color: #198754;
          }
          
          .stat-value {
            font-weight: 700;
            font-size: 32px;
            margin: 10px 0;
            text-align: center;

          }
          
          .stat-label {
            font-size: 14px;
            font-weight: 500;
          }
          
          .stat-progress {
            margin-top: 10px;
          }
          
          .progress-sm {
            height: 6px;
            border-radius: 6px;
          }
          
          .stat-link {
            color: var(--bs-primary);
            font-weight: 500;
            font-size: 14px;
            text-decoration: none;
            display: inline-block;
          }
          
          /* Summary Card */
          .summary-card {
            border-radius: 12px;
          }
          
          .total-container {
            padding: 15px 0;
          }
          
          .total-circle {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            background: linear-gradient(135deg, #4361ee, var(--bs-primary));
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 28px;
            font-weight: 700;
            margin: 0 auto;
            box-shadow: 0 5px 15px rgba(67, 97, 238, 0.3);
          }
          
          .summary-indicator {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            margin-right: 10px;
          }
          
          .summary-indicator.pending {
            background-color: #ffc107;
          }
          
          .summary-indicator.progress {
            background-color: #0d6efd;
          }
          
          .summary-indicator.completed {
            background-color: #198754;
          }
          
          .summary-text {
            font-weight: 500;
          }
          
          .summary-count {
            font-weight: 600;
          }
          
          .summary-percentage {
            font-size: 12px;
            font-weight: 500;
            padding: 2px 6px;
            border-radius: 10px;
            background-color: #f0f2f5;
          }
          
          /* Quick Action Cards */
          .quick-action-card {
            display: flex;
            align-items: center;
            padding: 1.25rem;
            border-radius: 12px;
            background-color: #fff;
            box-shadow: 0 2px 12px rgba(0,0,0,0.05);
            transition: all 0.3s ease;
            text-decoration: none;
            color: inherit;
            border: 1px solid #e9ecef;
            height: 100%;
          }
          
          .quick-action-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.1);
          }
          
          .quick-action-icon {
            width: 60px;
            height: 60px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 1rem;
            flex-shrink: 0;
          }
          
          .audit-card .quick-action-icon {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .reaudit-card .quick-action-icon {
            background-color: rgba(220, 53, 69, 0.1);
            color: #dc3545;
          }
          
          .quick-action-content h5 {
            color: #344767;
            font-weight: 600;
          }
          
          /* Responsive Adjustments */
          @media (max-width: 992px) {
            .progress-stat-group {
              width: 100%;
              margin-top: 2rem;
            }
          }
          
          @media (max-width: 768px) {
            .total-circle {
              width: 80px;
              height: 80px;
              font-size: 22px;
            }
          }
          
          @media (max-width: 576px) {
            .dashboard-header {
              border-radius: 0;
            }
            
            .greeting {
              font-size: 1.25rem;
            }
            
            .quick-action-card {
              flex-direction: column;
              text-align: center;
            }
            
            .quick-action-icon {
              margin-right: 0;
              margin-bottom: 1rem;
            }
          }
        `}
      </style>
    </Container>
  );
}

export default JBranchAuditorDashboard;
